import { z } from 'zod';

/**
 * Phase 25 — Tab Equipe cross-boundary type contract.
 *
 * Backend `EventoEquipeService.getEquipe(actor, eventoId)` and frontend
 * `EventoEquipeTabComponent` share this contract.
 *
 * Funcao order MONTADOR > TECNICO > CARREGADOR per CONTEXT.md "specifics" —
 * the backend orders SQL groups by this enum order (NOT alphabetically) so
 * the UI renders MONTADORES → TÉCNICOS → CARREGADORES top-down.
 *
 * Pitfall 3 reminder: `userId` is REQUIRED — it is the `avaliadoId` target
 * for AvaliacaoCtaComponent (Phase 23). One Profissional may have multiple
 * Users (1-to-many) but the canonical avaliação target is the first user.
 * Sending `profissionalId` to AvaliacaoCtaComponent would cause cross-row
 * self-rating failures and 400s.
 */

/**
 * Funcao Zod schema for runtime validation. The TS `Funcao` type lives in
 * `./enums` (avoid collision per project convention — see candidatura.ts L210-212).
 *
 * Exported under two names for backwards-compat with the planning doc:
 * - `EquipeFuncaoSchema` is the canonical zod schema name used internally.
 * - `FuncaoEnum` is the alias the plan + downstream consumers reference.
 */
export const EquipeFuncaoSchema = z.enum(['MONTADOR', 'TECNICO', 'CARREGADOR']);
export const FuncaoEnum = EquipeFuncaoSchema;
// `Funcao` TS type is NOT re-exported here — it is owned by `./enums` to keep
// a single source of truth across the package (mirrors candidatura.ts L210-212).
// To get the type: `import type { Funcao } from '@lance-x/shared';`

export const EquipeMemberSchema = z.object({
  candidaturaId: z.string(),
  profissionalId: z.string(),
  /**
   * The User.id of the profissional — Pitfall 3: this is the avaliadoId
   * target for AvaliacaoCtaComponent. Backend resolves via
   * `profissional.users[0]?.id` (Phase 13 pattern).
   */
  userId: z.string(),
  nome: z.string(),
  fotoUrl: z.string().nullable(),
  contato: z.string().nullable(),
  mediaAvaliacao: z.number().nullable(), // null when no avaliações yet
  totalAvaliacoes: z.number().int().nonnegative(),
});
export type EquipeMember = z.infer<typeof EquipeMemberSchema>;

export const EquipeGroupSchema = z.object({
  funcao: FuncaoEnum,
  members: z.array(EquipeMemberSchema),
});
export type EquipeGroup = z.infer<typeof EquipeGroupSchema>;

export const EquipeResponseSchema = z.object({
  groups: z.array(EquipeGroupSchema),
});
export type EquipeResponse = z.infer<typeof EquipeResponseSchema>;

// --- Phase 32 Plan 04 — cursor-paginated equipe endpoint ---

/**
 * Query parameters for GET /eventos/:id/equipe/page.
 * `limit` is coerced from string to int (controller passes @Query as string).
 */
export const EquipePageQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type EquipePageQuery = z.infer<typeof EquipePageQuerySchema>;

/**
 * Response shape for GET /eventos/:id/equipe/page.
 * Groups contain only the members present on this page; empty groups are omitted.
 * nextCursor is null when there are no further pages.
 */
export const EquipePageResponseSchema = z.object({
  groups: z.array(EquipeGroupSchema),
  nextCursor: z.string().nullable(),
});
export type EquipePageResponse = z.infer<typeof EquipePageResponseSchema>;
